import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { Box, Button, Grid, TextField, Typography, Paper, Stack, Chip, FormControl, InputLabel, Select, MenuItem } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useTranslation } from 'react-i18next'
import AdSlot from '../components/AdSlot'
import { maybeAttachAuthorization } from '@mahendran/shared-frontend-config'
import { buildApiUrl } from '../lib/apiBase'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'
import { VideoSeries, VideoSubmissionForm } from '../types/video'

const fetchWithAuth = async (url: string, options: RequestInit = {}) => {
    const headers = new Headers(options.headers);
    const credentials = options.credentials || 'include';
    maybeAttachAuthorization(headers, credentials as RequestCredentials);
    const res = await fetch(url, { ...options, headers, credentials });
    return res;
}

const SubmitVideo: React.FC = () => {
    const { t } = useTranslation()
    const { toastPromise } = useToast()
    const toastMessages = useToastMessages()
    const { register, handleSubmit, formState: { errors }, reset, control, watch } = useForm<VideoSubmissionForm>({
        defaultValues: {
            title: '',
            youtubeUrl: '',
            tags: '',
            description: '',
            seriesSlug: '',
            orderIndex: undefined,
            publishedAt: null,
            thumbnailOverride: ''
        }
    })

    const [submitting, setSubmitting] = React.useState(false)
    const pendingSubmission = React.useRef(false)
    const [series, setSeries] = React.useState<VideoSeries[]>([])
    const [loadingSeries, setLoadingSeries] = React.useState(true)
    const navigate = useNavigate()

    // Watch series selection to show/hide series-related fields
    const selectedSeriesSlug = watch('seriesSlug')

    // Load available series
    React.useEffect(() => {
        const loadSeries = async () => {
            try {
                const res = await fetchWithAuth(buildApiUrl('/video-series'))
                if (res.ok) {
                    const data = await res.json()
                    setSeries(data)
                }
            } catch (error) {
                console.error('Failed to load series:', error)
            } finally {
                setLoadingSeries(false)
            }
        }
        loadSeries()
    }, [])

    const onSubmit = async (data: VideoSubmissionForm) => {
        if (pendingSubmission.current) return
        pendingSubmission.current = true
        setSubmitting(true)
        const body = {
            title: data.title,
            youtubeUrl: data.youtubeUrl,
            tags: data.tags,
            description: data.description,
            seriesSlug: data.seriesSlug || undefined,
            orderIndex: data.seriesSlug && Number.isFinite(data.orderIndex) ? data.orderIndex : undefined,
            publishedAt: data.seriesSlug && data.publishedAt ? data.publishedAt.toISOString() : undefined,
            thumbnailOverride: data.seriesSlug ? data.thumbnailOverride || undefined : undefined
        }
        const promise = fetchWithAuth(buildApiUrl('/video-suggestions'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then(async (res) => {
            if (!res.ok) {
                let msg = ''
                try { const j = await res.clone().json(); msg = j?.detail || j?.title || '' } catch { /* ignore */ }
                if (!msg) { try { msg = await res.text() } catch { /* ignore */ } }
                throw new Error(msg || `HTTP ${res.status}`)
            }
            return res
        })

        await toastPromise(promise, {
            loading: 'Reiche ein...',
            success: () => {
                const selectedSeries = series.find((s) => s.slug === data.seriesSlug)
                navigate('/', {
                    replace: true,
                    state: {
                        submissionSuccess: {
                            title: data.title,
                            youtubeUrl: data.youtubeUrl,
                            tags: data.tags,
                            description: data.description,
                            seriesTitle: selectedSeries?.title || undefined
                        }
                    }
                })
                reset()
                return toastMessages.videoSubmitted()
            },
            error: (e: any) => {
                return e?.message || toastMessages.videoSubmitError()
            },
        }).catch(() => {
            // The toast already displays the error; retain inputs for a retry.
        }).finally(() => {
            pendingSubmission.current = false
            setSubmitting(false)
        })
    }

    return (
        <>
            <Box sx={{ py: 4 }}>
                <Typography variant="h4" gutterBottom>{t('submit.title', 'Video vorschlagen')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {t('submit.subtitle', 'Teile spannende Videos – wir kuratieren sie für die Community.')}
                </Typography>

                <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('submit.fields.title', 'Titel')}
                                    {...register('title', { required: true, minLength: 3 })}
                                    error={!!errors.title}
                                    helperText={errors.title ? t('submit.errors.title', 'Bitte einen gültigen Titel angeben (min. 3 Zeichen).') : ' '}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('submit.fields.url', 'YouTube-URL')}
                                    placeholder="https://www.youtube.com/watch?v=..."
                                    {...register('youtubeUrl', {
                                        required: true,
                                        pattern: /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/i
                                    })}
                                    error={!!errors.youtubeUrl}
                                    helperText={errors.youtubeUrl ? t('submit.errors.url', 'Bitte eine gültige YouTube-URL angeben.') : ' '}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('submit.fields.tags', 'Tags (Komma-getrennt)')}
                                    placeholder={t('submit.fields.tagsPlaceholder', 'z.B. Visa, Webdev, iPhone')}
                                    {...register('tags')}
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    {['Visa', 'Webdev', 'iPhone', 'Alltag'].map(x => <Chip key={x} label={x} size="small" />)}
                                </Stack>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t('submit.fields.description', 'Beschreibung (optional)')}
                                    multiline
                                    minRows={4}
                                    {...register('description')}
                                />
                            </Grid>

                            {/* Series Selection */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="submission-series-label">{t('submit.fields.series', 'Serie (optional)')}</InputLabel>
                                    <Controller
                                        name="seriesSlug"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                labelId="submission-series-label"
                                                label={t('submit.fields.series', 'Serie (optional)')}
                                                disabled={loadingSeries}
                                            >
                                                <MenuItem value="">
                                                    <em>{t('submit.fields.noSeries', 'Keine Serie')}</em>
                                                </MenuItem>
                                                {series.map((s) => (
                                                    <MenuItem key={s.slug} value={s.slug}>
                                                        {s.title}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Grid>

                            {/* Series-related fields - only show when a series is selected */}
                            {selectedSeriesSlug && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label={t('submit.fields.orderIndex', 'Reihenfolge in Serie')}
                                            placeholder="1"
                                            {...register('orderIndex', { valueAsNumber: true })}
                                        />
                                    </Grid>

                                    <Grid item xs={12} sm={6}>
                                        <Controller
                                            name="publishedAt"
                                            control={control}
                                            rules={{ validate: value => !selectedSeriesSlug || !value || Number.isFinite(value.getTime()) || 'Bitte ein gültiges Datum angeben.' }}
                                            render={({ field }) => (
                                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                    <DatePicker
                                                        {...field}
                                                        label={t('submit.fields.publishedAt', 'Veröffentlichungsdatum')}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                error: !!errors.publishedAt,
                                                                helperText: errors.publishedAt?.message
                                                            }
                                                        }}
                                                    />
                                                </LocalizationProvider>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label={t('submit.fields.thumbnailOverride', 'Custom Thumbnail URL (optional)')}
                                            placeholder="https://example.com/thumbnail.jpg"
                                            {...register('thumbnailOverride')}
                                        />
                                    </Grid>
                                </>
                            )}

                            <Grid item xs={12}>
                                <Button type="submit" variant="contained" disabled={submitting}>{t('submit.cta', 'Abschicken')}</Button>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                <Box sx={{ mt: 4 }}>
                    <AdSlot slotName="submit-bottom" height={250} />
                </Box>
            </Box>
        </>
    )
}

export default SubmitVideo